import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { debugMode } from './useDataManager';
import {
  useCacheBustRequests,
  useHasExecuted,
  useIsAcquired,
  useMarkExecution,
  useRelease,
} from './getters';
import { isFlagEnabled } from 'debug/flags';
import { FLAGS } from 'debug/identifiers';
import { useTriggerRefetch } from './getters';
import { orange } from '@kizen/kds/Colors';

export const useManagedQuery = ({
  dashletId,
  queryKey,
  queryFn,
  enabled = true,
  additionalParams,
}) => {
  const cacheBustRequests = useCacheBustRequests();
  const hasExecuted = useHasExecuted(dashletId);
  const isAcquired = useIsAcquired(dashletId);
  const release = useRelease(dashletId);
  const markExecution = useMarkExecution(dashletId);
  const triggerRefetch = useTriggerRefetch();
  const skipCache = isFlagEnabled(FLAGS.ALWAYS_REFRESH_DASHLET);

  const cacheBustRequestsTracker = useRef(cacheBustRequests);
  cacheBustRequestsTracker.current = cacheBustRequests;

  const isEnabled = enabled && (isAcquired || hasExecuted);

  const { data, isLoading, isRefetching, isFetchedAfterMount, isSuccess } =
    useQuery({
      queryKey,
      queryFn: () => {
        const isBustingCache =
          skipCache || cacheBustRequestsTracker.current?.has(dashletId);

        return queryFn(isBustingCache);
      },
      enabled: isEnabled,
      ...additionalParams,

      // Disable the built-in refetching mechanism because the data manager
      // will handle it.
      refetchOnWindowFocus: false,
      staleTime: isAcquired ? 0 : Infinity, // when acquired we need to reset the cache
    });

  useEffect(() => {
    if (isSuccess && isEnabled) {
      release(dashletId);
      markExecution(dashletId);
    }
  }, [isSuccess, dashletId, release, markExecution, isEnabled]);

  const aggregateIsLoading = isLoading || (!isAcquired && !data && enabled);
  const aggregateIsRefetching =
    isRefetching || (!isAcquired && !data && enabled);

  // Detect if queryKeyChanged
  const oldQueryKey = useRef(queryKey.toString());
  useEffect(() => {
    if (oldQueryKey.current !== queryKey.toString()) {
      if (debugMode) {
        console.log(
          `%c[DATA MANAGER] - query key changed for ${dashletId}`,
          `color: ${orange['orange-500']};`
        );
      }
      oldQueryKey.current = queryKey.toString();
      triggerRefetch(dashletId);
    }
  }, [queryKey, dashletId, triggerRefetch]);

  useEffect(() => {
    if (debugMode && (aggregateIsLoading || aggregateIsRefetching)) {
      console.group(
        `%c[DATA MANAGER] - loading conditions for ${dashletId}`,
        `color: ${orange['orange-300']};`
      );
      console.log({
        isLoading,
        isAcquired,
        enabled,
        hasData: Boolean(data),
        aggregateIsLoading,
        aggregateIsRefetching,
      });
      console.groupEnd();
    } else if (debugMode) {
      console.log(
        `%c[DATA MANAGER] - loading finished for ${dashletId}`,
        `color: ${orange['orange-300']};`
      );
    }
  }, [
    dashletId,
    data,
    isLoading,
    isRefetching,
    hasExecuted,
    enabled,
    isAcquired,
    aggregateIsLoading,
    aggregateIsRefetching,
  ]);

  return {
    data,
    isLoading: aggregateIsLoading,
    isRefetching: aggregateIsRefetching,
    hasExecuted,
    isFetchedAfterMount,
  };
};
